import { MaintenanceRequest } from '../../types/properties';
import { Wrench, AlertTriangle, Clock, CheckCircle2, ChevronRight, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface MaintenanceRequestCardProps {
  request: MaintenanceRequest;
  propertyName?: string;
  onClick?: () => void;
}

export function MaintenanceRequestCard({ request, propertyName, onClick }: MaintenanceRequestCardProps) {
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'emergency': return 'text-[var(--t-error)] bg-[var(--t-error)]/10 border-[var(--t-error)]/20';
      case 'high': return 'text-[var(--t-warning)] bg-[var(--t-warning-dim)] border-[var(--t-warning)]/20';
      case 'medium': return 'text-[var(--t-info)] bg-[var(--t-info-dim)] border-[var(--t-info)]/20';
      default: return 'text-[var(--t-text-muted)] bg-[var(--t-surface-hover)] border-[var(--t-border-subtle)]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-[var(--t-success)] bg-[var(--t-success-dim)]';
      case 'in-progress': return 'text-[var(--t-primary)] bg-[var(--t-primary-dim)]';
      case 'assigned': return 'text-[var(--t-info)] bg-[var(--t-info-dim)]';
      case 'cancelled': return 'text-[var(--t-text-muted)] bg-[var(--t-surface-hover)]';
      default: return 'text-[var(--t-warning)] bg-[var(--t-warning-dim)]';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={14} />;
      case 'in-progress': return <Wrench size={14} />;
      default: return <Clock size={14} />;
    }
  };

  return (
    <div
      onClick={onClick}
      className={`bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl p-4 flex flex-col gap-3 transition-all ${onClick ? 'cursor-pointer hover:border-[var(--t-primary)] hover:shadow-md' : ''}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${getUrgencyColor(request.urgency)}`}>
              {request.urgency}
            </span>
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${getStatusColor(request.status)}`}>
              {getStatusIcon(request.status)}
              {request.status.replace('-', ' ')}
            </span>
          </div>
          <h4 className="font-bold text-[var(--t-text)] truncate" title={request.title}>{request.title}</h4>
          {propertyName && (
            <p className="text-xs text-[var(--t-text-muted)] mt-0.5 truncate">{propertyName}</p>
          )}
        </div>
        {onClick && (
          <ChevronRight size={18} className="text-[var(--t-border-hover)] shrink-0 mt-2" />
        )}
      </div>

      {request.description && (
        <p className="text-sm text-[var(--t-text-muted)] line-clamp-2 leading-relaxed">
          {request.description}
        </p>
      )}

      <div className="mt-2 pt-3 border-t border-[var(--t-border-subtle)] flex flex-wrap items-center justify-between gap-y-2 gap-x-4 text-xs font-medium text-[var(--t-text-secondary)]">
        <div className="flex items-center gap-1.5 capitalize">
          <Wrench size={12} className="text-[var(--t-text-muted)]" />
          {request.category}
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-[var(--t-text-muted)]" />
          {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
        </div>
        {request.assigned_to && (
           <div className="flex items-center gap-1.5">
             <User size={12} className="text-[var(--t-primary)]" />
             <span className="text-[var(--t-primary)]">{request.assigned_to}</span>
           </div>
        )}
      </div>
    </div>
  );
}
